import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EarningMethod } from '../../../models/stamp_card';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-earning-method-dialog',
  templateUrl: './earning-method-dialog.component.html',
  styleUrls: ['./earning-method-dialog.component.css'],
})
export class EarningMethodDialogComponent implements OnInit {
  form!: FormGroup;
  titleFormControl!: FormControl;
  descriptionFormControl!: FormControl;
  pointsAwardedFormControl!: FormControl;
  titlePlaceholder = 'e.g. Purchase coffee';
  descriptionPlaceholder = 'e.g. Earn points for every coffee purchase';

  constructor(
    public matDialogRef: MatDialogRef<EarningMethodDialogComponent>,
    public fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.setDynamicPlaceholders();

    this.titleFormControl = new FormControl('', [Validators.required]);
    this.descriptionFormControl = new FormControl('', [Validators.required]);
    this.pointsAwardedFormControl = new FormControl(null, [Validators.min(1)]);

    this.form = this.fb.group({
      title: this.titleFormControl,
      description: this.descriptionFormControl,
      pointsAwarded: this.pointsAwardedFormControl,
    });

    // If editing existing earning method
    if (this.data?.existingEarningMethod) {
      const method = this.data.existingEarningMethod;
      this.titleFormControl.setValue(method.title);
      this.descriptionFormControl.setValue(method.description);
      this.pointsAwardedFormControl.setValue(method.pointsAwarded);
    }
  }

  primaryTapped() {
    if (this.form?.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const earningMethod: EarningMethod = {
      title: this.titleFormControl.value,
      description: this.descriptionFormControl.value,
      pointsAwarded: this.pointsAwardedFormControl.value,
    };

    this.matDialogRef.close(earningMethod);
  }

  get isEditing(): boolean {
    return !!this.data?.existingEarningMethod;
  }

  get dialogTitle(): string {
    return this.isEditing ? 'Edit Earning Method' : 'Add Earning Method';
  }

  setDynamicPlaceholders() {
    const merchantType = this.authService
      .getNestedUserProperty('merchant', 'type')
      ?.toLowerCase();

    // Define placeholders based on merchant type for earning methods
    const placeholders = {
      cafe: {
        title: 'e.g. Purchase coffee',
        description: 'e.g. Earn points for every coffee purchase',
      },
      restaurant: {
        title: 'e.g. Dine in',
        description: 'e.g. Earn points for every meal at our restaurant',
      },
      bakery: {
        title: 'e.g. Buy pastries',
        description: 'e.g. Earn points for every bakery item purchased',
      },
      beauty: {
        title: 'e.g. Book treatment',
        description: 'e.g. Earn points for every beauty treatment booked',
      },
      barber: {
        title: 'e.g. Get haircut',
        description: 'e.g. Earn points for every haircut or grooming service',
      },
      'pubs & bars': {
        title: 'e.g. Order drinks',
        description: 'e.g. Earn points for every drink order',
      },
      shops: {
        title: 'e.g. Make purchase',
        description: 'e.g. Earn points for every purchase in store',
      },
      online: {
        title: 'e.g. Place order',
        description: 'e.g. Earn points for every online order',
      },
    };

    // Set placeholders based on merchant type, fallback to cafe defaults
    const selectedPlaceholders =
      placeholders[merchantType as keyof typeof placeholders] ||
      placeholders.cafe;
    this.titlePlaceholder = selectedPlaceholders.title;
    this.descriptionPlaceholder = selectedPlaceholders.description;
  }
}
